import React from 'react';
import _ from 'lodash';
import { contentfulModels, components } from '@glowforge/ui-common';
import MediaCard from './media-card';

import './media-cards-list.less';

const { widgetDefaultProps, widgetPropType } = contentfulModels;
const { Button } = components;

const ITEMS_PER_PAGE = 8;
// 400 pixel scroll offset for mobile infinite scroll.
const SCROLL_OFFSET = 400;

/**
 * What can you print?
 * Renders a grid of cards that are clickable to open a dialog/modal.
 * The cards are paged, in that we show multiples of ITEMS_PER_PAGE at a time.
 * ITEMS_PER_PAGE more cards are visible each time a user clicks a show more button.
 */
class MediaCardsList extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      page: 1,
      hasLoadedAllItems: false,
    };
    // memoized array for visible items.
    this.visibleItems = [];
    this.incrementPage = this.incrementPage.bind(this);
    this.getVisibleItems = this.getVisibleItems.bind(this);
    this.getListItems = this.getListItems.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
    this.removeScrollListener = this.removeScrollListener.bind(this);

    // setup scroll listener
    this.gridRef = React.createRef();
    this.throttledScroll = _.throttle(this.handleScroll, 200);
  }

  componentDidMount() {
    // Add scroll listener to grid on client
    this.gridRef.current.addEventListener('scroll', this.throttledScroll);
  }

  componentWillUnmount() {
    // Cancel scroll listner
    this.removeScrollListener();
  }

  /**
   * Helper for getting listItems from props.
   */
  getListItems() {
    return _.get(this.props, 'sections[0].fields.listItems');
  }

  /**
   * Memoized getter for paged items.  We show ITEMS_PER_PAGE cards
   * at a time in the desktop grid view.
   * @param {Array} items full array of cards/items
   */
  getVisibleItems() {
    const items = this.getListItems();
    const numPagedItems = this.state.page * ITEMS_PER_PAGE;
    if (this.visibleItems.length < numPagedItems) {
      this.visibleItems = items.slice(0, numPagedItems);
    }
    return this.visibleItems;
  }

  removeScrollListener() {
    this.throttledScroll.cancel();
    this.gridRef.current.removeEventListener('scroll', this.throttledScroll);
  }

  /**
   * Handle grid scroll.  Used to infinite scroll mobile view.
   */
  handleScroll() {
    const items = this.getListItems();
    // Have we loaded all the items yet?
    if (this.visibleItems.length < items.length) {
      const { clientWidth, scrollWidth, scrollLeft } = this.gridRef.current;
      // if grid is overflowed horizontally, we're on mobile.
      if (scrollWidth > clientWidth) {
        const rightEdge = scrollLeft + clientWidth;
        // If rightEdge of grid is within SCROLL_OFFSET pixels of end, load more items
        if (scrollWidth - rightEdge < SCROLL_OFFSET) {
          this.incrementPage();
        }
      }
    } else {
      // all items have been loaded, no longer need to scroll.
      this.removeScrollListener();
    }
  }

  /**
   * Increment the page to trigger a re-render and show the
   * next set of items.
   */
  incrementPage() {
    const page = this.state.page + 1;
    this.setState({
      page,
    });
  }

  render() {
    // Extract data from Contentful
    const textStrings = _.get(this.props, 'model.textStrings');
    const title = textStrings['media-card-title'];
    const seeMoreText = textStrings['media-card-see-more'];
    const swipeToSeeMoreText = textStrings['media-card-swipe-to-see-more'];
    const listItems = this.getListItems();

    // get paged/memoized list of items
    const items = this.getVisibleItems(listItems);

    // If there are more items to be shown, render a show more button.
    const moreItems = items.length < listItems.length;
    const showMoreButton = moreItems ? (
      <Button className='button' type='secondary' onClick={this.incrementPage}>{seeMoreText}</Button>
    ) : null;
    const showMoreClasses = moreItems ? 'moreItems' : '';

    // Only render visible items in main view
    const cards = _.map(items, _.property('fields')).map(fields => (
      <MediaCard
        className={_.camelCase(fields.title)}
        key={fields.title}
        image={fields.images[0]}
        title={fields.title}
        text={fields.text} />
    ));

    return (
      <div className='MediaCardsList'>
        <h2 className='title'>{title}</h2>
        <div className='cards-grid' ref={this.gridRef} >
          {cards}
        </div>
        <div className={`showMore ${showMoreClasses}`}>
          <div className='text'>{swipeToSeeMoreText}</div>
          {showMoreButton}
        </div>
      </div>
    );
  }
}

MediaCardsList.propTypes = widgetPropType;

MediaCardsList.defaultProps = widgetDefaultProps;

export default MediaCardsList;



// WEBPACK FOOTER //
// ./app/components/landing/media-cards-list.jsx